import { Link } from "react-router-dom";
import { gbp, ukDate } from "../../lib/api";

export const shortLabel = (label) => (label || "").replace(/^per /, "/ ").replace("/ litre", "/ litre");

function SourceLine({ rec, testId, backState }) {
  if (!rec) return null;
  const text = `${rec.date ? ukDate(rec.date) : ""}${rec.source === "price_list" ? " · Manual Uploaded Price" : " · Invoice Price"}`;
  return rec.invoice_id
    ? <Link to={`/invoices/${rec.invoice_id}`} state={backState} className="text-xs text-emerald-700 hover:underline" data-testid={testId}>{text}</Link>
    : <span className="text-xs text-slate-400" data-testid={testId}>{text}</span>;
}

function Secondary({ card, testId }) {
  if (!card) return null;
  const list = (card.unit_prices || []).filter((u) => u.key !== "unit");
  if (list.length) {
    return (
      <div className="mt-0.5 space-y-0.5" data-testid={testId}>
        {list.map((u) => <p key={u.key} className="font-mono text-sm font-semibold text-slate-700" data-testid={`${testId}-${u.key}`}>{gbp(u.value)} <span className="text-xs font-sans text-slate-500">{shortLabel(u.label)}</span></p>)}
      </div>
    );
  }
  if (card.value == null || card.label === "per pack" || card.label === "unit unknown") return null;
  const main = card.pack_price ?? card.price;
  if (Math.abs(card.value - main) < 0.0001 && (card.unit || "").toLowerCase() === (card.label || "").replace("per ", "")) return null;
  return <p className="font-mono text-sm font-semibold text-slate-700" data-testid={testId}>{gbp(card.value)} <span className="text-xs font-sans text-slate-500">{shortLabel(card.label)}</span></p>;
}

export const unitLabel = (u) => (u && u.length <= 5 && u === u.toUpperCase() ? u : (u || "").toLowerCase());
const MEASURE = new Set(["kg", "kgs", "g", "l", "lt", "ltr", "litre", "liter", "ml", "cl"]);
const SINGLE = new Set(["each", "ea", "piece", "pc", "pcs", "unit", "item", "can", "bottle", "btl", "tin", "jar", "portion"]);
const mainUnit = (card) => {
  const u = (card?.unit || "").toLowerCase().split(" ")[0];
  if (SINGLE.has(u) && card.pack_price != null && card.price != null && Math.abs(card.pack_price - card.price) > 0.001) return "pack";
  if (MEASURE.has(u) && card.pack_size && card.pack_size.toLowerCase() !== u) return card.pack_size.toLowerCase();
  return unitLabel(card?.unit) || "pack";
};

function ExtremeCard({ title, card, tone, testId, backState }) {
  const color = tone === "low" ? "text-emerald-700" : "text-rose-700";
  const unit = mainUnit(card);
  return (
    <div className="stat-card" data-testid={`${testId}-card`}>
      <p className="text-xs uppercase tracking-wider text-slate-500">{title}</p>
      {!card ? <p className={`mt-1 font-display text-2xl font-bold ${color}`} data-testid={testId}>—</p> : (
        <>
          <p className={`mt-1 font-display text-2xl font-bold ${color}`} data-testid={testId}>{gbp(card.pack_price ?? card.price)}<span className="text-sm font-sans font-medium text-slate-400"> / {unit}</span></p>
          <Secondary card={card} testId={`${testId}-normalised`} />
          <p className="mt-1 text-xs text-slate-600 truncate" data-testid={`${testId}-supplier`}>{card.supplier_name}</p>
          <SourceLine rec={card} testId={`${testId}-source`} backState={backState} />
        </>
      )}
    </div>
  );
}

export default function PriceCards({ product, latest, latestAcross, stats, supplierName, backState }) {
  const latestCard = latest ? { ...latest, ...latest.comparable } : null;
  const acrossCard = latestAcross ? { ...latestAcross, ...latestAcross.comparable } : null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6" data-testid="price-cards">
      <div className="stat-card" data-testid="latest-price-card">
        <p className="text-xs uppercase tracking-wider text-slate-500">Latest price</p>
        <p className="mt-1 font-display text-2xl font-bold" data-testid="latest-price">{gbp(product.latest_price)}{(latest?.unit || product.unit) ? <span className="text-sm font-sans font-medium text-slate-400"> / {unitLabel(latest?.unit || product.unit)}</span> : null}</p>
        <Secondary card={latestCard} testId="latest-price-normalised" />
        <div className="mt-1" data-testid="latest-price-date"><SourceLine rec={latestCard || { date: product.latest_price_date, source: product.latest_price_source }} testId="latest-price-source" backState={backState} /></div>
        <p className="text-xs text-slate-600 truncate" data-testid="latest-price-supplier">{latest?.supplier_name || supplierName}</p>
        {acrossCard && (
          <div className="mt-2 border-t border-dashed border-slate-200 pt-2" data-testid="latest-across-suppliers">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Latest across suppliers</p>
            <p className="font-mono text-sm font-semibold text-slate-800" data-testid="latest-across-price">{gbp(acrossCard.pack_price ?? acrossCard.price)}<span className="text-xs font-sans font-medium text-slate-400"> / {mainUnit(acrossCard)}</span>{acrossCard.value != null && acrossCard.label !== "per pack" && acrossCard.label !== "unit unknown" && <span className="ml-1 text-xs font-sans text-slate-500" data-testid="latest-across-normalised">{gbp(acrossCard.value)} {shortLabel(acrossCard.label)}</span>}</p>
            <p className="text-xs text-slate-600 truncate" data-testid="latest-across-supplier">{acrossCard.supplier_name}</p>
            <SourceLine rec={acrossCard} testId="latest-across-source" backState={backState} />
          </div>
        )}
      </div>
      <ExtremeCard title="Lowest" card={stats?.lowest} tone="low" testId="lowest-price" backState={backState} />
      <ExtremeCard title="Highest" card={stats?.highest} tone="high" testId="highest-price" backState={backState} />
      {(stats?.has_mixed_units || stats?.ambiguous_count > 0) && (
        <p className="sm:col-span-3 text-xs text-slate-500" data-testid="price-cards-note">
          {stats.suppliers_compared > 1 ? `Compared ${shortLabel(stats.unit_label).replace("/ ", "per ")} across ${stats.suppliers_compared} suppliers. ` : ""}
          {stats.has_mixed_units ? "Some records use a different measurable basis and were left out. " : ""}
          {stats.ambiguous_count ? <span data-testid="ambiguous-count">{stats.ambiguous_count} line{stats.ambiguous_count > 1 ? "s" : ""} skipped — billing unit not confirmed.</span> : null}
        </p>
      )}
    </div>
  );
}
